import { RefObject, useCallback, useState } from 'react'
import { useTimelineMagnetize } from 'hooks/use-timeline-magnetize'
import { useSelector } from 'react-redux'
import { projectDataSelectors } from 'store/features/projectData/selectors'

const DEFAULT_MAX_WIDTH = Infinity
const DEFAULT_MIN_WIDTH = 0

export const useHorizontalResize = ({
  ref,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidthForLeftResize = DEFAULT_MAX_WIDTH,
  maxWidthForRightResize = DEFAULT_MAX_WIDTH,
  onResizeEnd,
  onResizeStart,
  handleLeftResize,
  handleRightResize,
  scrollableContainerRef,
  left
}: {
  ref: React.RefObject<HTMLElement | null>
  minWidth?: number
  maxWidthForLeftResize?: number
  maxWidthForRightResize?: number
  onResizeEnd?: () => void
  onResizeStart?: () => void
  handleLeftResize?: (newWidth: number) => void
  handleRightResize?: (newWidth: number) => void
  scrollableContainerRef: RefObject<HTMLElement | null>
  left: number
}) => {
  const [isResizing, setIsResizing] = useState(false)
  const magnetizingBreakpointsExcludingActive = useSelector(
    projectDataSelectors.magnetizingBreakpointsExcludingActive
  )

  const { magnetizeLeftResize, magnetizeRightResize, hideMagnetizingLine } =
    useTimelineMagnetize()

  const handleResizeStart = useCallback(() => {
    setIsResizing(true)
    onResizeStart?.()
  }, [onResizeStart])

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false)
    onResizeEnd?.()
    hideMagnetizingLine()
  }, [onResizeEnd, hideMagnetizingLine])

  const currentItemInitialRight = left + (ref.current?.offsetWidth ?? 0)

  const handleResize = useCallback(
    (
      e: MouseEvent | TouchEvent,
      resizeCallback: ({
        deltaX,
        startWidth
      }: {
        deltaX: number
        startWidth: number
      }) => void
    ) => {
      e.preventDefault()
      e.stopPropagation()
      if (!ref.current || !scrollableContainerRef?.current) return
      handleResizeStart()

      const startClientX = 'clientX' in e ? e.clientX : e.touches[0].clientX
      const scrollableContainerScrollLeft =
        scrollableContainerRef.current.scrollLeft

      const startX = scrollableContainerScrollLeft + startClientX
      const startWidth = ref.current.offsetWidth

      const handleMouseMove = (e: MouseEvent) => {
        if (!scrollableContainerRef.current) return

        e.preventDefault()
        e.stopPropagation()

        const scrollableContainerScrollLeft =
          scrollableContainerRef.current.scrollLeft
        const currentX = scrollableContainerScrollLeft + e.clientX
        const deltaX = currentX - startX

        resizeCallback({ deltaX, startWidth })
      }

      const handleTouchMove = (e: TouchEvent) => {
        if (!scrollableContainerRef.current) return

        e.preventDefault()
        e.stopPropagation()

        const scrollableContainerScrollLeft =
          scrollableContainerRef.current.scrollLeft
        const currentX = scrollableContainerScrollLeft + e.touches[0].clientX
        const deltaX = currentX - startX

        resizeCallback({ deltaX, startWidth })
      }

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchend', handleMouseUp)
        handleResizeEnd()
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false
      })
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchend', handleMouseUp)
    },
    [handleResizeStart, handleResizeEnd, ref, scrollableContainerRef]
  )

  const onRightResize = useCallback(
    (e: MouseEvent | TouchEvent) => {
      handleResize(e, ({ deltaX, startWidth }) => {
        const magnetizedDeltaX = magnetizeRightResize(
          deltaX,
          currentItemInitialRight,
          magnetizingBreakpointsExcludingActive
        )

        const newWidth = Math.max(
          Math.min(startWidth + magnetizedDeltaX, maxWidthForRightResize),
          minWidth
        )

        handleRightResize?.(newWidth)
      })
    },
    [
      handleResize,
      handleRightResize,
      minWidth,
      maxWidthForRightResize,
      magnetizeRightResize,
      currentItemInitialRight,
      magnetizingBreakpointsExcludingActive
    ]
  )

  const onLeftResize = useCallback(
    (e: MouseEvent | TouchEvent) => {
      handleResize(e, ({ deltaX, startWidth }) => {
        const magnetizedDeltaX = magnetizeLeftResize(
          deltaX,
          left,
          magnetizingBreakpointsExcludingActive
        )

        const newWidth = Math.max(
          Math.min(startWidth - magnetizedDeltaX, maxWidthForLeftResize),
          minWidth
        )

        handleLeftResize?.(newWidth)
      })
    },
    [
      handleResize,
      handleLeftResize,
      minWidth,
      maxWidthForLeftResize,
      magnetizeLeftResize,
      left,
      magnetizingBreakpointsExcludingActive
    ]
  )

  return {
    isResizing,
    onRightResize,
    onLeftResize
  }
}
